/**
 * ADF (Atlassian Document Format) <-> TipTap/ProseMirror JSON.
 *
 * The editor schema mirrors ADF closely; the differences handled here are
 * renamed types (rule/horizontalRule, strong/bold, em/italic), mention
 * text/label, task items (inline content in ADF, paragraph in TipTap) and
 * media, which the editor models as a single `attachment` atom.
 * Anything the editor can't edit (expand, status, date, extensions, ...) is
 * carried through untouched as an opaque `adfBlock`/`adfInline` atom so an
 * edit never drops content created in Jira.
 */

export interface DocNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
}

export interface AdfDoc {
  type: 'doc';
  version: 1;
  content: DocNode[];
}

const MARK_TO_EDITOR: Record<string, string> = { strong: 'bold', em: 'italic' };
const MARK_TO_ADF: Record<string, string> = { bold: 'strong', italic: 'em' };
const EDITOR_MARKS = new Set(['bold', 'italic', 'underline', 'strike', 'code', 'link', 'textColor', 'subsup']);
/** Block-level ADF marks (alignment, indentation) kept on paragraphs/headings. */
const BLOCK_MARK_TYPES = new Set(['alignment', 'indentation']);

/**
 * Teammates without Jira are mentioned as "@Name" linked to mailto:<email>
 * (Jira can only mention its own users); the editor shows them as mentions
 * with id "portal:<email>". The portal server alerts them on Slack.
 */
export const PORTAL_MENTION_PREFIX = 'portal:';

export function portalMentionEmail(node: DocNode): string | null {
  if (node.type !== 'text' || !node.text?.startsWith('@') || node.marks?.length !== 1) return null;
  const mark = node.marks[0]!;
  const href = typeof mark.attrs?.href === 'string' ? mark.attrs.href : '';
  const match = mark.type === 'link' ? /^mailto:([^?]+)$/i.exec(href) : null;
  return match ? decodeURIComponent(match[1]!) : null;
}

let idCounter = 0;
export function localId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  idCounter += 1;
  return `local-${Date.now().toString(36)}-${idCounter}`;
}

// ── ADF → editor ────────────────────────────────────────────────────

export function adfToEditor(adf: unknown): DocNode {
  const doc = adf as { type?: string; content?: DocNode[] } | null;
  const blocks = doc && doc.type === 'doc' && Array.isArray(doc.content) ? doc.content.flatMap(blockToEditor) : [];
  return { type: 'doc', content: blocks.length ? blocks : [{ type: 'paragraph' }] };
}

function blocksToEditor(nodes: DocNode[] | undefined): DocNode[] {
  const out = (nodes ?? []).flatMap(blockToEditor);
  return out.length ? out : [{ type: 'paragraph' }];
}

function withContent(node: DocNode, content: DocNode[]): DocNode {
  return content.length ? { ...node, content } : node;
}

function blockToEditor(n: DocNode): DocNode[] {
  switch (n.type) {
    case 'paragraph':
    case 'heading': {
      const attrs: Record<string, unknown> = n.type === 'heading' ? { level: Number(n.attrs?.level) || 1 } : {};
      const blockMarks = n.marks?.filter((m) => BLOCK_MARK_TYPES.has(m.type));
      if (blockMarks?.length) attrs.adfMarks = blockMarks;
      return [withContent({ type: n.type, ...(Object.keys(attrs).length ? { attrs } : {}) }, inlinesToEditor(n.content))];
    }
    case 'bulletList':
      return [{ type: 'bulletList', content: (n.content ?? []).map(listItemToEditor) }];
    case 'orderedList':
      return [{ type: 'orderedList', attrs: { start: Number(n.attrs?.order) || 1 }, content: (n.content ?? []).map(listItemToEditor) }];
    case 'blockquote':
      return [{ type: 'blockquote', content: blocksToEditor(n.content) }];
    case 'codeBlock': {
      const text = (n.content ?? []).map((c) => c.text ?? '').join('');
      return [withContent({ type: 'codeBlock', attrs: { language: (n.attrs?.language as string) || null } }, text ? [{ type: 'text', text }] : [])];
    }
    case 'rule':
      return [{ type: 'horizontalRule' }];
    case 'panel': {
      const { panelType, ...rest } = n.attrs ?? {};
      return [{ type: 'panel', attrs: { panelType: (panelType as string) || 'info', adfAttrs: Object.keys(rest).length ? rest : null }, content: blocksToEditor(n.content) }];
    }
    case 'table':
      return [{ type: 'table', attrs: { adfAttrs: n.attrs ?? null }, content: (n.content ?? []).map(rowToEditor) }];
    case 'taskList':
      return [taskListToEditor(n)];
    case 'mediaSingle': {
      const [media, ...extra] = n.content ?? [];
      if (!media || media.type !== 'media') return [opaqueBlock(n)];
      return [{
        type: 'attachment',
        attrs: {
          kind: 'single',
          media: media.attrs ?? {},
          layout: (n.attrs?.layout as string) || 'center',
          width: n.attrs?.width ?? null,
          extra: extra.length ? extra : null,
        },
      }];
    }
    case 'mediaGroup':
      return (n.content ?? []).filter((m) => m.type === 'media').map((m) => ({
        type: 'attachment',
        attrs: { kind: 'group', media: m.attrs ?? {}, layout: null, width: null, extra: null },
      }));
    default:
      return [opaqueBlock(n)];
  }
}

function opaqueBlock(n: DocNode): DocNode {
  return { type: 'adfBlock', attrs: { json: n } };
}

function listItemToEditor(item: DocNode): DocNode {
  const content = blocksToEditor(item.content);
  // TipTap list items must start with a paragraph
  if (content[0]?.type !== 'paragraph') content.unshift({ type: 'paragraph' });
  return { type: 'listItem', content };
}

function rowToEditor(row: DocNode): DocNode {
  return {
    type: 'tableRow',
    content: (row.content ?? []).map((cell) => ({
      type: cell.type === 'tableHeader' ? 'tableHeader' : 'tableCell',
      attrs: {
        colspan: Number(cell.attrs?.colspan) || 1,
        rowspan: Number(cell.attrs?.rowspan) || 1,
        colwidth: Array.isArray(cell.attrs?.colwidth) ? cell.attrs.colwidth : null,
        background: (cell.attrs?.background as string) || null,
      },
      content: blocksToEditor(cell.content),
    })),
  };
}

function taskListToEditor(list: DocNode): DocNode {
  const items: DocNode[] = [];
  for (const child of list.content ?? []) {
    if (child.type === 'taskItem') {
      items.push({
        type: 'taskItem',
        attrs: { checked: child.attrs?.state === 'DONE', localId: (child.attrs?.localId as string) || localId() },
        content: [withContent({ type: 'paragraph' }, inlinesToEditor(child.content))],
      });
    } else if (child.type === 'taskList') {
      // ADF nests a sub-list as a sibling; TipTap nests it inside the previous item
      const nested = taskListToEditor(child);
      const prev = items[items.length - 1];
      if (prev) prev.content = [...(prev.content ?? []), nested];
      else items.push({ type: 'taskItem', attrs: { checked: false, localId: localId() }, content: [{ type: 'paragraph' }, nested] });
    }
  }
  if (!items.length) items.push({ type: 'taskItem', attrs: { checked: false, localId: localId() }, content: [{ type: 'paragraph' }] });
  return { type: 'taskList', attrs: { localId: (list.attrs?.localId as string) || localId() }, content: items };
}

function inlinesToEditor(nodes: DocNode[] | undefined): DocNode[] {
  const out: DocNode[] = [];
  for (const n of nodes ?? []) {
    const portalEmail = portalMentionEmail(n);
    if (portalEmail) {
      out.push({ type: 'mention', attrs: { id: `${PORTAL_MENTION_PREFIX}${portalEmail}`, label: n.text!.replace(/^@+/, '') } });
      continue;
    }
    if (n.type === 'text') {
      if (!n.text) continue;
      const marks = (n.marks ?? [])
        .map((m) => ({ type: MARK_TO_EDITOR[m.type] ?? m.type, attrs: m.attrs }))
        .filter((m) => EDITOR_MARKS.has(m.type))
        .map((m) => (m.attrs ? { type: m.type, attrs: pickMarkAttrs(m.type, m.attrs) } : { type: m.type }));
      out.push(marks.length ? { type: 'text', text: n.text, marks } : { type: 'text', text: n.text });
    } else if (n.type === 'hardBreak') {
      out.push({ type: 'hardBreak' });
    } else if (n.type === 'mention') {
      out.push({
        type: 'mention',
        attrs: { id: n.attrs?.id ?? null, label: String(n.attrs?.text ?? '').replace(/^@+/, '') || null },
      });
    } else {
      out.push({ type: 'adfInline', attrs: { json: n } });
    }
  }
  return out;
}

function pickMarkAttrs(type: string, attrs: Record<string, unknown>): Record<string, unknown> {
  if (type === 'link') return { href: attrs.href ?? null };
  if (type === 'textColor') return { color: attrs.color ?? null };
  if (type === 'subsup') return { type: attrs.type ?? 'sup' };
  return {};
}

// ── editor → ADF ────────────────────────────────────────────────────

export function editorToAdf(doc: DocNode): AdfDoc {
  const content = blocksToAdf(doc.content);
  // Trailing empty paragraphs are editor artifacts
  while (content.length && isEmptyParagraph(content[content.length - 1]!)) content.pop();
  return { type: 'doc', version: 1, content };
}

function isEmptyParagraph(n: DocNode): boolean {
  return n.type === 'paragraph' && !(n.content?.length);
}

function blocksToAdf(nodes: DocNode[] | undefined): DocNode[] {
  const out: DocNode[] = [];
  for (const n of nodes ?? []) {
    if (n.type === 'attachment') {
      const a = n.attrs ?? {};
      const media = mediaAttrsToAdf(a.media);
      if (!media) continue; // still uploading or failed — never send a dangling media id
      if (a.kind === 'group') {
        const prev = out[out.length - 1];
        const node = { type: 'media', attrs: media };
        if (prev?.type === 'mediaGroup') prev.content!.push(node);
        else out.push({ type: 'mediaGroup', content: [node] });
      } else {
        const attrs: Record<string, unknown> = { layout: (a.layout as string) || 'center' };
        if (typeof a.width === 'number') attrs.width = a.width;
        out.push({ type: 'mediaSingle', attrs, content: [{ type: 'media', attrs: media }, ...((a.extra as DocNode[] | null) ?? [])] });
      }
      continue;
    }
    out.push(...blockToAdf(n));
  }
  return out;
}

function mediaAttrsToAdf(raw: unknown): Record<string, unknown> | null {
  const m = (raw ?? {}) as Record<string, unknown>;
  if (m.type === 'external' && typeof m.url === 'string') return m;
  if (typeof m.id !== 'string' || !m.id) return null;
  return { ...m, type: (m.type as string) || 'file', collection: (m.collection as string) ?? '' };
}

function nonEmptyBlocks(nodes: DocNode[] | undefined): DocNode[] {
  const out = blocksToAdf(nodes);
  return out.length ? out : [{ type: 'paragraph' }];
}

function blockToAdf(n: DocNode): DocNode[] {
  switch (n.type) {
    case 'paragraph':
    case 'heading': {
      const node: DocNode = { type: n.type };
      if (n.type === 'heading') node.attrs = { level: Number(n.attrs?.level) || 1 };
      const content = inlinesToAdf(n.content);
      if (content.length) node.content = content;
      const blockMarks = n.attrs?.adfMarks as DocNode['marks'] | undefined;
      if (blockMarks?.length) node.marks = blockMarks;
      return [node];
    }
    case 'bulletList':
      return [{ type: 'bulletList', content: (n.content ?? []).map((li) => ({ type: 'listItem', content: nonEmptyBlocks(li.content) })) }];
    case 'orderedList': {
      const start = Number(n.attrs?.start) || 1;
      return [{
        type: 'orderedList',
        ...(start !== 1 ? { attrs: { order: start } } : {}),
        content: (n.content ?? []).map((li) => ({ type: 'listItem', content: nonEmptyBlocks(li.content) })),
      }];
    }
    case 'blockquote':
      return [{ type: 'blockquote', content: nonEmptyBlocks(n.content) }];
    case 'codeBlock': {
      const text = (n.content ?? []).map((c) => c.text ?? '').join('');
      const node: DocNode = { type: 'codeBlock' };
      const language = n.attrs?.language as string | null | undefined;
      if (language) node.attrs = { language };
      if (text) node.content = [{ type: 'text', text }];
      return [node];
    }
    case 'horizontalRule':
      return [{ type: 'rule' }];
    case 'panel': {
      const extra = (n.attrs?.adfAttrs as Record<string, unknown> | null) ?? {};
      return [{ type: 'panel', attrs: { ...extra, panelType: (n.attrs?.panelType as string) || 'info' }, content: nonEmptyBlocks(n.content) }];
    }
    case 'table': {
      const attrs = (n.attrs?.adfAttrs as Record<string, unknown> | null) ?? { isNumberColumnEnabled: false, layout: 'default' };
      return [{ type: 'table', attrs, content: (n.content ?? []).map(rowToAdf) }];
    }
    case 'taskList':
      return [taskListToAdf(n)];
    case 'adfBlock':
      return n.attrs?.json ? [n.attrs.json as DocNode] : [];
    default:
      return [];
  }
}

function rowToAdf(row: DocNode): DocNode {
  return {
    type: 'tableRow',
    content: (row.content ?? []).map((cell) => {
      const attrs: Record<string, unknown> = {};
      const colspan = Number(cell.attrs?.colspan) || 1;
      const rowspan = Number(cell.attrs?.rowspan) || 1;
      if (colspan !== 1) attrs.colspan = colspan;
      if (rowspan !== 1) attrs.rowspan = rowspan;
      if (Array.isArray(cell.attrs?.colwidth)) attrs.colwidth = cell.attrs.colwidth;
      if (cell.attrs?.background) attrs.background = cell.attrs.background;
      return {
        type: cell.type === 'tableHeader' ? 'tableHeader' : 'tableCell',
        ...(Object.keys(attrs).length ? { attrs } : {}),
        content: nonEmptyBlocks(cell.content),
      };
    }),
  };
}

function taskListToAdf(list: DocNode): DocNode {
  const content: DocNode[] = [];
  for (const item of list.content ?? []) {
    const blocks = item.content ?? [];
    const inline: DocNode[] = [];
    const nested: DocNode[] = [];
    for (const b of blocks) {
      if (b.type === 'taskList') nested.push(taskListToAdf(b));
      else if (b.type === 'paragraph' || b.type === 'heading') {
        if (inline.length && b.content?.length) inline.push({ type: 'hardBreak' });
        inline.push(...inlinesToAdf(b.content));
      }
    }
    content.push({
      type: 'taskItem',
      attrs: { localId: (item.attrs?.localId as string) || localId(), state: item.attrs?.checked ? 'DONE' : 'TODO' },
      ...(inline.length ? { content: inline } : {}),
    });
    content.push(...nested);
  }
  return { type: 'taskList', attrs: { localId: (list.attrs?.localId as string) || localId() }, content };
}

function inlinesToAdf(nodes: DocNode[] | undefined): DocNode[] {
  const out: DocNode[] = [];
  for (const n of nodes ?? []) {
    if (n.type === 'text') {
      if (!n.text) continue;
      const marks = (n.marks ?? [])
        .filter((m) => EDITOR_MARKS.has(m.type))
        .map((m) => {
          const type = MARK_TO_ADF[m.type] ?? m.type;
          const attrs = m.attrs ? pickMarkAttrs(m.type, m.attrs) : {};
          return Object.keys(attrs).length && (m.type === 'link' || m.type === 'textColor' || m.type === 'subsup') ? { type, attrs } : { type };
        })
        .filter((m) => m.type !== 'link' || typeof (m as { attrs?: { href?: unknown } }).attrs?.href === 'string');
      out.push(marks.length ? { type: 'text', text: n.text, marks } : { type: 'text', text: n.text });
    } else if (n.type === 'hardBreak') {
      out.push({ type: 'hardBreak' });
    } else if (n.type === 'mention') {
      if (!n.attrs?.id) continue;
      const label = String(n.attrs.label ?? '').replace(/^@+/, '');
      const id = String(n.attrs.id);
      if (id.startsWith(PORTAL_MENTION_PREFIX)) {
        const email = id.slice(PORTAL_MENTION_PREFIX.length);
        out.push({ type: 'text', text: `@${label || email}`, marks: [{ type: 'link', attrs: { href: `mailto:${email}` } }] });
      } else {
        out.push({ type: 'mention', attrs: { id, text: `@${label}`, accessLevel: '' } });
      }
    } else if (n.type === 'adfInline') {
      if (n.attrs?.json) out.push(n.attrs.json as DocNode);
    }
  }
  return out;
}

// ── helpers ─────────────────────────────────────────────────────────

/** True when the ADF has no text, mentions, media or other meaningful content. */
export function isAdfEmpty(doc: AdfDoc | DocNode | null | undefined): boolean {
  if (!doc) return true;
  const visit = (n: DocNode): boolean => {
    if (n.type === 'text') return !(n.text ?? '').trim();
    if (n.type === 'doc' || n.type === 'paragraph' || n.type === 'heading') return (n.content ?? []).every(visit);
    if (n.type === 'hardBreak') return true;
    return false;
  };
  return visit(doc as DocNode);
}

/** Editor docs that still hold uploads in flight (or awaiting an issue to upload into). */
export function pendingAttachmentKeys(doc: DocNode): string[] {
  const keys: string[] = [];
  const visit = (n: DocNode) => {
    if (n.type === 'attachment' && typeof n.attrs?.pendingKey === 'string') keys.push(n.attrs.pendingKey);
    n.content?.forEach(visit);
  };
  visit(doc);
  return keys;
}

/** Point pending attachments at their uploaded media ids; drops ones that failed. */
export function resolvePendingAttachments(doc: DocNode, uploaded: Map<string, string>): DocNode {
  const visit = (n: DocNode): DocNode | null => {
    if (n.type === 'attachment' && typeof n.attrs?.pendingKey === 'string') {
      const mediaId = uploaded.get(n.attrs.pendingKey);
      if (!mediaId) return null;
      return { ...n, attrs: { ...n.attrs, pendingKey: null, localSrc: null, uploading: false, media: { type: 'file', id: mediaId, collection: '' } } };
    }
    if (!n.content) return n;
    return { ...n, content: n.content.map(visit).filter((c): c is DocNode => c !== null) };
  };
  return visit(doc) ?? doc;
}
